import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { toast } from 'sonner';
import { Plus, Trash2, Power, PowerOff, Users, Copy, Pencil } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function PromoCodeManager() {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [selectedPromo, setSelectedPromo] = useState(null);
  const [promoUsers, setPromoUsers] = useState([]);
  const [pricingPlans, setPricingPlans] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    code: '',
    type: 'credit',
    credit_amount: '',
    discount_percentage: '',
    applicable_plans: [],
    description: '',
    max_uses: '',
    expires_at: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchPromos();
      fetchPricingPlans();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchPricingPlans = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/pricing/plans`);
      setPricingPlans(response.data.plans);
    } catch (error) {
      console.error('Failed to load pricing plans');
    }
  };

  const fetchPromos = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/admin/promo/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPromos(response.data.promos || []);
    } catch (error) {
      console.error('Failed to load promo codes:', error);
      toast.error('Failed to load promo codes');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      const data = {
        code: formData.code.toUpperCase(),
        type: formData.type,
        description: formData.description,
        max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
        expires_at: formData.expires_at || null,
        applicable_plans: formData.applicable_plans
      };

      if (formData.type === 'credit') {
        data.credit_amount = parseInt(formData.credit_amount);
      } else {
        data.discount_percentage = parseInt(formData.discount_percentage);
      }

      if (editingPromo) {
        // Update existing promo
        await axios.put(`${BACKEND_URL}/api/admin/promo/${editingPromo.code}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(`Promo code ${data.code} updated!`);
        setEditingPromo(null);
      } else {
        // Create new promo
        await axios.post(`${BACKEND_URL}/api/admin/promo/create`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(`Promo code ${data.code} created!`);
      }
      
      setShowCreateForm(false);
      setFormData({
        code: '',
        type: 'credit',
        credit_amount: '',
        discount_percentage: '',
        applicable_plans: [],
        description: '',
        max_uses: '',
        expires_at: ''
      });
      fetchPromos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save promo code');
    }
  };

  const handleEdit = (promo) => {
    setEditingPromo(promo);
    setFormData({
      code: promo.code,
      type: promo.type,
      credit_amount: promo.credit_amount || '',
      discount_percentage: promo.discount_percentage || '',
      applicable_plans: promo.applicable_plans || [],
      description: promo.description,
      max_uses: promo.max_uses || '',
      expires_at: promo.expires_at ? promo.expires_at.split('T')[0] : ''
    });
    setShowCreateForm(true);
  };

  const handleToggleActive = async (code, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${BACKEND_URL}/api/admin/promo/${code}`, 
        { is_active: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(`Promo ${code} ${!currentStatus ? 'activated' : 'deactivated'}`);
      fetchPromos();
    } catch (error) {
      console.error('Failed to update promo:', error);
      toast.error('Failed to update promo status');
    }
  };

  const handleDelete = async (code) => {
    if (!window.confirm(`Delete promo code ${code}? This cannot be undone.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/admin/promo/${code}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Promo code ${code} deleted`);
      fetchPromos();
    } catch (error) {
      console.error('Failed to delete promo:', error);
      toast.error('Failed to delete promo code');
    }
  };

  const viewUsers = async (code) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/admin/promo/${code}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPromoUsers(response.data.users || []);
      setSelectedPromo(code);
    } catch (error) {
      console.error('Failed to load users:', error);
      toast.error('Failed to load users');
    }
  };

  const copyPromoLink = (code) => {
    const link = `${window.location.origin}/promo/${code}`;
    navigator.clipboard.writeText(link);
    toast.success('Promo link copied!');
  };

  if (loading) {
    return <div className="p-8">Loading promo codes...</div>;
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h1 className="text-2xl sm:text-3xl font-bold">Promo Code Manager</h1>
        <Button onClick={() => setShowCreateForm(!showCreateForm)} className="w-full sm:w-auto">
          <Plus className="mr-2 h-4 w-4" />
          Create New Promo
        </Button>
      </div>

      {/* Create/Edit Form */}
      {showCreateForm && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{editingPromo ? 'Edit Promo Code' : 'Create New Promo Code'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Promo Code *</Label>
                  <Input
                    value={formData.code}
                    onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                    placeholder="e.g., WELCOME50"
                    required
                  />
                </div>

                <div>
                  <Label>Type *</Label>
                  <Select value={formData.type} onValueChange={(val) => setFormData({...formData, type: val})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="credit">Free Credits</SelectItem>
                      <SelectItem value="discount">Discount %</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {formData.type === 'credit' && (
                  <div>
                    <Label>Credit Amount *</Label>
                    <Input
                      type="number"
                      value={formData.credit_amount}
                      onChange={(e) => setFormData({...formData, credit_amount: e.target.value})}
                      placeholder="50"
                      required={formData.type === 'credit'}
                    />
                  </div>
                )}

                {formData.type === 'discount' && (
                  <>
                    <div>
                      <Label>Discount % *</Label>
                      <Input
                        type="number"
                        value={formData.discount_percentage}
                        onChange={(e) => setFormData({...formData, discount_percentage: e.target.value})}
                        placeholder="20"
                        required={formData.type === 'discount'}
                      />
                    </div>
                    <div>
                      <Label>Applicable Plans *</Label>
                      <Select 
                        value={formData.applicable_plans[0] || 'all'} 
                        onValueChange={(val) => {
                          if (val === 'all') {
                            setFormData({...formData, applicable_plans: ['all']});
                          } else {
                            setFormData({...formData, applicable_plans: [val]});
                          }
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select plan" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Plans (Credit + Course Enrollments)</SelectItem>
                          
                          {/* Credit Plans */}
                          <div className="px-2 py-1 text-xs font-semibold text-gray-500 bg-gray-50">
                            Credit Plans
                          </div>
                          {pricingPlans.filter(p => p.category === 'Credits').map(plan => (
                            <SelectItem key={plan.id} value={plan.id}>
                              {plan.name}
                            </SelectItem>
                          ))}
                          
                          {/* Course Plans */}
                          <div className="px-2 py-1 text-xs font-semibold text-gray-500 bg-gray-50 mt-2">
                            Course Enrollment Plans
                          </div>
                          {pricingPlans.filter(p => p.category === 'Courses').map(plan => (
                            <SelectItem key={plan.id} value={plan.id}>
                              {plan.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}

                <div>
                  <Label>Max Uses (optional)</Label>
                  <Input
                    type="number"
                    value={formData.max_uses}
                    onChange={(e) => setFormData({...formData, max_uses: e.target.value})}
                    placeholder="100"
                  />
                </div>

                <div>
                  <Label>Expires At (optional)</Label>
                  <Input
                    type="date"
                    value={formData.expires_at}
                    onChange={(e) => setFormData({...formData, expires_at: e.target.value})}
                  />
                </div>

                <div className="col-span-2">
                  <Label>Description *</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Launch week special offer"
                    required
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <Button type="submit">{editingPromo ? 'Update Promo Code' : 'Create Promo Code'}</Button>
                <Button type="button" variant="outline" onClick={() => {
                  setShowCreateForm(false);
                  setEditingPromo(null);
                  setFormData({
                    code: '',
                    type: 'credit',
                    credit_amount: '',
                    discount_percentage: '',
                    applicable_plans: [],
                    description: '',
                    max_uses: '',
                    expires_at: ''
                  });
                }}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Promo List */}
      <div className="grid gap-4">
        {promos.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-gray-500">
              No promo codes yet. Create your first one!
            </CardContent>
          </Card>
        ) : (
          promos.map((promo) => (
            <Card key={promo.id} className={!promo.is_active ? 'opacity-60' : ''}>
              <CardContent className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                  <div className="flex-1 w-full">
                    <div className="flex items-center gap-2 sm:gap-3 mb-2 flex-wrap">
                      <h3 className="text-xl sm:text-2xl font-bold break-all">{promo.code}</h3>
                      <span className={`px-2 py-1 rounded text-sm ${
                        promo.type === 'credit' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                      }`}>
                        {promo.type === 'credit' 
                          ? `${promo.credit_amount} Credits` 
                          : `${promo.discount_percentage}% Off`}
                      </span>
                      {!promo.is_active && (
                        <span className="px-2 py-1 rounded text-sm bg-red-100 text-red-800">
                          Inactive
                        </span>
                      )}
                    </div>
                    
                    <p className="text-gray-600 mb-3">{promo.description}</p>
                    
                    <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                      <span>Uses: {promo.current_uses}{promo.max_uses ? `/${promo.max_uses}` : ''}</span>
                      {promo.expires_at && (
                        <span>Expires: {new Date(promo.expires_at).toLocaleDateString()}</span>
                      )}
                      {promo.type === 'discount' && promo.applicable_plans && (
                        <span className="text-blue-600">
                          Plans: {promo.applicable_plans.includes('all') 
                            ? 'All Plans' 
                            : promo.applicable_plans.join(', ')}
                        </span>
                      )}
                    </div>

                    <div className="mt-3 overflow-x-auto">
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded whitespace-nowrap">
                        {window.location.origin}/promo/{promo.code}
                      </code>
                    </div>
                  </div>

                  <div className="flex gap-2 w-full sm:w-auto flex-wrap">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyPromoLink(promo.code)}
                      title="Copy link"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>

                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(promo)}
                      title="Edit promo"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>

                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => viewUsers(promo.code)}
                      title="View users"
                    >
                      <Users className="h-4 w-4" />
                    </Button>

                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleToggleActive(promo.code, promo.is_active)}
                      title={promo.is_active ? 'Deactivate' : 'Activate'}
                    >
                      {promo.is_active ? (
                        <PowerOff className="h-4 w-4" />
                      ) : (
                        <Power className="h-4 w-4" />
                      )}
                    </Button>

                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(promo.code)}
                      className="text-red-600 hover:bg-red-50"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Users Modal */}
      {selectedPromo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="max-w-2xl w-full max-h-[80vh] overflow-auto">
            <CardHeader>
              <CardTitle>Users who used {selectedPromo}</CardTitle>
            </CardHeader>
            <CardContent>
              {promoUsers.length === 0 ? (
                <p className="text-gray-500">No users have used this promo yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Mobile</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Degree</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Redeemed</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Benefit</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {promoUsers.map((user, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">{user.name}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{user.email}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{user.mobile_number}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            <div>{user.degree}</div>
                            <div className="text-xs text-gray-400">{user.study_year}</div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 text-xs rounded ${
                              user.status === 'Active' 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {user.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-500">
                            {new Date(user.redeemed_at).toLocaleDateString()}<br/>
                            {new Date(user.redeemed_at).toLocaleTimeString()}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {user.credits_granted && (
                              <span className="text-green-600 font-medium">{user.credits_granted} credits</span>
                            )}
                            {user.discount_applied && (
                              <span className="text-blue-600 font-medium">{user.discount_applied}% off</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <Button className="mt-4" onClick={() => setSelectedPromo(null)}>
                Close
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

export default PromoCodeManager;
